import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/utils/cn";
import { ResizableSidebarContext, type ResizableSidebarContextValue } from "@/components/useResizableSidebar";

interface ResizableSidebarProps {
    children: ReactNode;
    /** Shown as vertical text in the collapsed strip. */
    title?: string;
    defaultWidth?: number;
    minWidth?: number;
    maxWidth?: number;
    /** Width (px) of the collapsed strip. */
    collapsedWidth?: number;
    /** Extra classes applied to the outer container div. */
    containerClassName?: string;
}

/**
 * A drag-to-resize, collapsible sidebar widget.
 *
 * Renders as a React Fragment — two sibling elements in the parent flex row:
 *   1. A grip handle (desktop only, cursor-col-resize)
 *   2. The sidebar container (full-width on mobile, dynamic width on desktop)
 *
 * Children can read sidebar state via `useResizableSidebar()`.
 *
 * @example
 * // Inside a `flex flex-col lg:flex-row` wrapper:
 * <ResizableSidebar title={t("common:shared_ui.leave_balance")} defaultWidth={280}>
 *   <LeaveBalance />
 * </ResizableSidebar>
 */
export const ResizableSidebar = ({
    children,
    title,
    defaultWidth = 280,
    minWidth = 200,
    maxWidth = 480,
    collapsedWidth = 40,
    containerClassName,
}: ResizableSidebarProps) => {
  const { t } = useTranslation(["common"]);
    const [width, setWidth] = useState(defaultWidth);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);

    // Ref-driven during drag to avoid per-frame React re-renders
    const containerRef = useRef<HTMLDivElement>(null);
    const widthRef = useRef(defaultWidth);

    useEffect(() => {
        const check = () => setIsDesktop(window.innerWidth >= 1024);
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    const startResize = (e: React.MouseEvent) => {
        if (isCollapsed) return;
        e.preventDefault();
        setIsResizing(true);

        const startX = e.clientX;
        const startWidth = widthRef.current;

        const onMove = (e: MouseEvent) => {
            const next = Math.max(minWidth, Math.min(maxWidth, startWidth + (startX - e.clientX)));
            widthRef.current = next;
            if (containerRef.current) containerRef.current.style.width = `${next}px`;
        };

        const onUp = () => {
            setWidth(widthRef.current);
            setIsResizing(false);
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };

        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    const onToggle = () => setIsCollapsed((v) => !v);
    const collapsed = isCollapsed && isDesktop;

    const ctxValue: ResizableSidebarContextValue = {
        isCollapsed: collapsed,
        onToggle,
        width,
        isDesktop,
        hasSidebar: true,
    };

    return (
        <>
            {/* ── Grip handle (desktop only) ──────────────────────────────── */}
            <div
                className={cn(
                    "hidden lg:flex lg:order-2 lg:self-stretch items-center justify-center w-5 shrink-0 select-none",
                    collapsed ? "cursor-default" : "cursor-col-resize group",
                )}
                onMouseDown={startResize}
            >
                {!collapsed && (
                    <div
                        className={cn(
                            "rounded-full transition-all duration-150 sticky top-1/2 -translate-y-1/2",
                            isResizing
                                ? "w-1 h-14 bg-primary/50"
                                : "w-[3px] h-8 bg-gray-300 group-hover:w-1 group-hover:h-12 group-hover:bg-primary/40",
                        )}
                    />
                )}
            </div>

            {/* ── Sidebar container ───────────────────────────────────────── */}
            <div
                ref={containerRef}
                className={cn(
                    "order-last lg:order-3 w-full self-stretch flex flex-col rounded-sm overflow-hidden border",
                    isResizing
                        ? "border-primary/40 shadow-[0_0_0_3px_rgba(100,56,255,0.07)]"
                        : "border-border",
                    !isResizing && "transition-[width,border-color,box-shadow] duration-200",
                    containerClassName,
                )}
                style={isDesktop ? { width: collapsed ? collapsedWidth : width } : undefined}
            >
                {collapsed ? (
                    /* ── Collapsed strip ────────────────────────────────────── */
                    <div className="flex flex-col items-center py-3 h-full bg-gray-50/70 gap-2">
                        {/* Expand button */}
                        <button
                            onClick={onToggle}
                            title={t("common:a11y.expand")}
                            className={cn(
                                "flex items-center justify-center w-7 h-7 rounded-sm shrink-0",
                                "text-gray-400 border border-transparent",
                                "hover:text-primary hover:bg-white hover:border-border hover:shadow-xs",
                                "transition-all duration-150",
                            )}
                        >
                            <ChevronLeft size={14} />
                        </button>

                        {/* Divider */}
                        <div className="w-4 h-px bg-border shrink-0" />

                        {/* Vertical title */}
                        {title && (
                            <div className="flex-1 flex items-center justify-center overflow-hidden min-h-0">
                                <span
                                    className="text-[9px] font-semibold tracking-[0.2em] uppercase text-gray-400 whitespace-nowrap"
                                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                                >
                                    {title}
                                </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <ResizableSidebarContext.Provider value={ctxValue}>
                        {children}
                    </ResizableSidebarContext.Provider>
                )}
            </div>
        </>
    );
};
